import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { date, dateTime, money } from "@/lib/format";
import type { PlatformDashboard as Payload } from "@/lib/types";
import { Card, PageHeader, Skeleton, Stat, Table } from "@/components/ui";
import { useAuth } from "@/state/auth";
import { P } from "@/lib/permissions";

/**
 * One dashboard, two audiences.
 *
 * Operations and platform administration ask the same first question, "what is
 * the state of the business right now", so they read the same endpoint. What
 * differs is what each may do next, and that is decided by permissions rather
 * than by building a second screen that drifts out of step with this one.
 */
export function PlatformDashboard({ scope }: { scope: "platform" | "ops" }) {
  const { can } = useAuth();
  const path = scope === "platform" ? "/v1/admin/dashboard" : "/v1/ops/dashboard";

  const { data, isLoading } = useQuery({
    queryKey: ["platform-dashboard", scope],
    queryFn: () => api.get<Payload>(`${path}?days=30`),
    refetchInterval: 60_000,
  });

  const stats = data?.stats;
  const peak = Math.max(1, ...(data?.volume ?? []).map((d) => d.shipments));

  return (
    <>
      <PageHeader
        title={scope === "platform" ? "Platform overview" : "Control room"}
        subtitle={
          scope === "platform"
            ? "Every account, every shipment, last 30 days."
            : "Live operational picture across all merchants."
        }
      />

      <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Shipments today" value={stats?.shipments_today ?? 0} loading={isLoading} />
        <Stat label="In transit" value={stats?.in_transit ?? 0} loading={isLoading} />
        <Stat
          label="Needs attention"
          value={stats?.exceptions ?? 0}
          tone={(stats?.exceptions ?? 0) > 0 ? "bad" : "neutral"}
          loading={isLoading}
          hint="Exceptions and failed attempts"
        />
        <Stat
          label="Active accounts"
          value={stats?.active_accounts ?? 0}
          hint={`${stats?.new_accounts_30d ?? 0} new in 30 days`}
          loading={isLoading}
        />
      </div>

      {/* Margin is the number the business is run on, so it gets its own row
          rather than being one tile among eight. Cost is shown next to it
          because revenue without cost is a vanity metric. */}
      {can(P.viewBilling) && (
        <div className="mb-5 grid gap-4 sm:grid-cols-3">
          <Stat label="Revenue, 30 days" value={money(stats?.revenue_30d_cents)} loading={isLoading} />
          <Stat label="Carrier cost, 30 days" value={money(stats?.cost_30d_cents)} loading={isLoading} />
          <Stat
            label="Gross margin, 30 days"
            value={money(stats?.margin_30d_cents)}
            tone="good"
            hint={
              stats && stats.revenue_30d_cents > 0
                ? `${Math.round((stats.margin_30d_cents / stats.revenue_30d_cents) * 100)}% of revenue`
                : undefined
            }
            loading={isLoading}
          />
        </div>
      )}

      <Card className="mb-5">
        <div className="mb-4 flex items-baseline justify-between">
          <h2 className="text-lg font-bold tracking-tight">Daily volume</h2>
          <p className="text-sm text-ink-500">{stats?.shipments_30d ?? 0} shipments in 30 days</p>
        </div>
        {isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          // A bar per day, drawn with divs. A charting library is 40 KB for one
          // sparkline, and this reads exactly the same at a glance.
          <div className="flex h-32 items-end gap-1">
            {(data?.volume ?? []).map((day) => (
              <div key={day.day} className="group relative flex-1">
                <div
                  className="w-full rounded-t bg-brand/80 transition group-hover:bg-brand"
                  style={{ height: `${Math.max(2, (day.shipments / peak) * 128)}px` }}
                />
                <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 hidden -translate-x-1/2 whitespace-nowrap rounded-lg bg-ink-900 px-2 py-1 text-xs text-white group-hover:block">
                  {date(day.day)}: {day.shipments} shipment{day.shipments === 1 ? "" : "s"}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Only rendered for whoever can actually impersonate. An admin who
          cannot use the feature has no reason to read who has been inside a
          customer's account. */}
      {data?.recent_impersonations && (
        <>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-bold tracking-tight">Recent impersonations</h2>
            <Link to="/app/admin/users" className="text-sm font-semibold text-brand">
              Manage users
            </Link>
          </div>
          {data.recent_impersonations.length === 0 ? (
            <Card>
              <p className="text-sm text-ink-500">
                Nobody has acted as a customer. Every session that does is recorded here with a
                reason.
              </p>
            </Card>
          ) : (
            <Table head={["Admin", "Acted as", "Reason", "Started", "Ended"]}>
              {data.recent_impersonations.map((session) => (
                <tr key={session.id}>
                  <td className="ws-td font-medium">{session.actor_email}</td>
                  <td className="ws-td">{session.subject_email}</td>
                  <td className="ws-td text-ink-600">{session.reason}</td>
                  <td className="ws-td text-ink-500">{dateTime(session.started_at)}</td>
                  <td className="ws-td text-ink-500">
                    {session.ended_at ? dateTime(session.ended_at) : "Expired or still open"}
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </>
      )}
    </>
  );
}
